"use client";

import { LoaderCircle, Radio } from "lucide-react";
import { useLinkStatus } from "next/link";
import Link from "@/components/ui/Link";
import { useEffect, useRef } from "react";
import { cn } from "@/lib/cn";
import { compact } from "@/lib/format";
import { type StreamQuery, streamsHref } from "@/lib/stream-filters";
import { STREAM_PLATFORMS } from "@/lib/stream-platforms";
import { PlatformLogo } from "./PlatformLogo";
import { useUi } from "@/components/ui/use-ui";

function Pending() {
  const { pending } = useLinkStatus();
  const t = useUi();
  return pending ? <LoaderCircle size={14} className="animate-spin" aria-label={t.common.loading} /> : null;
}

/** "All platforms" plus each platform, with how many of its streams are live. */
export function platformChoices(counts: Record<string, number>) {
  return [
    { id: null, platform: null, name: "All platforms", logo: null, live: Object.values(counts).reduce((a, b) => a + b, 0) },
    ...STREAM_PLATFORMS.map((p) => ({ id: p.id, platform: p, name: p.name, logo: p.logo, live: counts[p.source] ?? 0 })),
  ];
}

/**
 * The platforms as a row of tabs (phones and tablets; StreamFilterPanel lists them on wider screens).
 * Switching keeps the other filters except the tag (each site has its own tags).
 */
export function PlatformTabs({ query, counts }: { query: StreamQuery; counts: Record<string, number> }) {
  const active = query.platform?.id ?? null;
  const msg = useUi();
  const nav = useRef<HTMLElement>(null);
  // On phones the row scrolls sideways: bring the chosen tab into view (without scrolling the page).
  useEffect(() => {
    const el = nav.current;
    const tab = el?.querySelector<HTMLElement>("[aria-current=page]");
    if (el && tab) el.scrollLeft = tab.offsetLeft - (el.clientWidth - tab.offsetWidth) / 2;
  }, [active]);
  const tabs = platformChoices(counts);
  return (
    <nav ref={nav} aria-label={msg.cams.streamPlatforms} className="no-scrollbar -mx-3 overflow-x-auto scroll-smooth px-3 md:mx-0 md:px-0">
      <ul className="flex w-max gap-2 pb-1">
        {tabs.map((t) => {
          const on = t.id === active;
          return (
            <li key={t.id ?? "all"}>
              <Link
                href={streamsHref({ ...query, platform: t.platform, tag: undefined })}
                scroll={false}
                aria-current={on ? "page" : undefined}
                className={cn(
                  "flex h-11 items-center gap-2.5 rounded-full pl-1.5 pr-4 text-[14px] transition-colors",
                  on ? "bg-white text-black" : "chip text-soft hover:text-fg",
                )}
              >
                {t.logo ? (
                  <PlatformLogo logo={t.logo} />
                ) : (
                  <span className={cn("grid size-[30px] place-items-center rounded-full", on ? "bg-brand-strong text-white" : "bg-brand/15 text-brand-soft")}>
                    <Radio size={15} />
                  </span>
                )}
                <span className="whitespace-nowrap">{t.id ? t.name : msg.cams.allPlatforms}</span>
                <span className={cn("tabular-nums text-[12.5px]", on ? "text-black/55" : "text-muted")}>{compact(t.live)}</span>
                <Pending />
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
